import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { User, Briefcase, Mail, Phone, Calendar, MapPin, ArrowLeft, Globe } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import Footer from '../components/common/Footer';
import { attendeeAPI } from '../api/endpoints';
import { getAttendeeName } from '../utils/attendeeHelpers';

const AttendeeDetail = () => {
  const { attendeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');

  // Fetch attendee details (public access, no auth required)
  const { data: attendeeData, isLoading, error } = useQuery({
    queryKey: ['attendee-detail', attendeeId, eventId],
    queryFn: async () => {
      try {
        // Use public endpoint that doesn't require authentication
        const response = await attendeeAPI.getPublicDetailNoAuth(attendeeId);

        // Handle different response structures
        if (response && response.attendee) {
          return response;
        } else if (response && response.data && response.data.attendee) {
          return response.data;
        } else {
          throw new Error('Invalid response structure from API');
        }
      } catch (err) {
        // Enhanced error handling for middleware issues
        if (err.response) {
          const status = err.response.status;
          const data = err.response.data;

          // Check if it's an HTML error response (middleware error)
          if (typeof data === 'string' && data.includes('<!DOCTYPE html>')) {
            throw new Error('System temporarily unavailable. Please try again in a few moments.');
          }

          // Handle specific HTTP status codes
          switch (status) {
            case 404:
              throw new Error('Attendee not found or QR code is invalid');
            case 429:
              throw new Error('Too many requests. Please wait a moment and try again.');
            case 500:
              throw new Error('Server error. Please try again later.');
            default:
              throw new Error(data?.message || 'Unable to load attendee details');
          }
        }

        // Network or other errors
        if (err.code === 'NETWORK_ERROR' || !err.response) {
          throw new Error('Network connection error. Please check your internet connection.');
        }

        throw err;
      }
    },
    enabled: !!attendeeId,
    retry: (failureCount, error) => {
      // Don't retry on 404 (not found) or 403 (forbidden)
      if (error?.message?.includes('not found') || error?.message?.includes('invalid')) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Helper function to get field value
  const getFieldValue = (attendee, fieldName) => {
    if (!attendee?.data || !Array.isArray(attendee.data)) return '';
    const field = attendee.data.find(d =>
      d.field?.field_name === fieldName ||
      d.custom_field?.field_name === fieldName
    );
    return field?.value || field?.field_value || '';
  };


  // Show loading while fetching attendee data
  if (isLoading) {
    return <Loading fullScreen />;
  }

  // Show error if attendee not found
  if (error || !attendeeData) {

    // Determine error type and styling
    const isSystemError = error?.message?.includes('System temporarily unavailable') ||
      error?.message?.includes('Server error');
    const isNetworkError = error?.message?.includes('Network connection error');
    const isRateLimitError = error?.message?.includes('Too many requests');

    const errorConfig = {
      bgGradient: isSystemError ? 'from-orange-50 to-yellow-100' :
        isNetworkError ? 'from-blue-50 to-indigo-100' :
          isRateLimitError ? 'from-purple-50 to-pink-100' :
            'from-red-50 to-pink-100',
      iconBg: isSystemError ? 'bg-orange-100' :
        isNetworkError ? 'bg-blue-100' :
          isRateLimitError ? 'bg-purple-100' :
            'bg-red-100',
      iconColor: isSystemError ? 'text-orange-600' :
        isNetworkError ? 'text-blue-600' :
          isRateLimitError ? 'text-purple-600' :
            'text-red-600',
      title: isSystemError ? 'System Temporarily Unavailable' :
        isNetworkError ? 'Connection Problem' :
          isRateLimitError ? 'Please Wait' :
            'Attendee Not Found',
      description: isSystemError ? 'Our system is experiencing temporary issues. Please try again in a few moments.' :
        isNetworkError ? 'Please check your internet connection.' :
          isRateLimitError ? 'You\'re making requests too quickly. Please wait a moment before trying again.' :
            'The attendee you\'re looking for doesn\'t exist or has been removed.'
    };

    return (
      <div className={`min-h-screen bg-gradient-to-br ${errorConfig.bgGradient} flex items-center justify-center p-4`}>
        <Card className="w-full max-w-md">
          <Card.Content>
            <div className="text-center space-y-6">
              <div className={`mx-auto w-16 h-16 ${errorConfig.iconBg} rounded-full flex items-center justify-center`}>
                <User className={`h-8 w-8 ${errorConfig.iconColor}`} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{errorConfig.title}</h1>
                <p className="text-gray-600 mb-4">
                  {errorConfig.description}
                </p>
                {error && !isSystemError && (
                  <div className={`text-sm ${errorConfig.iconColor} bg-gray-50 p-3 rounded-lg border-l-4 border-current`}>
                    <strong>Details:</strong> {error.message}
                  </div>
                )}
                {(isSystemError || isNetworkError || isRateLimitError) && (
                  <div className="text-xs text-gray-500 mt-3 bg-gray-50 p-2 rounded">
                    This is usually temporary. Try refreshing the page or scanning the QR code again.
                  </div>
                )}

              </div>
              <div className="flex flex-col gap-2">
                {(isSystemError || isNetworkError || isRateLimitError) && (
                  <Button
                    onClick={() => window.location.reload()}
                    className="w-full gap-2"
                  >
                    Try Again
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => window.history.length > 1 ? navigate(-1) : window.close()}
                  className="w-full gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {window.history.length > 1 ? 'Go Back' : 'Close'}
                </Button>
              </div>
            </div>
          </Card.Content>
        </Card>
      </div>
    );
  }

  const attendee = attendeeData.attendee;
  const event = attendee.event;

  // Extract attendee information
  const name = getAttendeeName(attendee);
  const email = getFieldValue(attendee, 'email');
  const phone = getFieldValue(attendee, 'phone');
  const organization = getFieldValue(attendee, 'organization') || getFieldValue(attendee, 'company');
  const position = getFieldValue(attendee, 'position') || getFieldValue(attendee, 'job_title');
  const country = getFieldValue(attendee, 'country') || getFieldValue(attendee, 'location');

  // Additional contact information
  const whatsapp = getFieldValue(attendee, 'phone_number') || getFieldValue(attendee, 'whatsapp');
  const website = getFieldValue(attendee, 'website') || getFieldValue(attendee, 'website_url');
  const linkedin = getFieldValue(attendee, 'linkedin') || getFieldValue(attendee, 'linkedin_url');
  const twitter = getFieldValue(attendee, 'twitter') || getFieldValue(attendee, 'twitter_handle');

  // Professional information
  const department = getFieldValue(attendee, 'department');
  const specialization = getFieldValue(attendee, 'specialization') || getFieldValue(attendee, 'field_of_study');
  const experience = getFieldValue(attendee, 'experience') || getFieldValue(attendee, 'years_experience');
  const skills = getFieldValue(attendee, 'skills') || getFieldValue(attendee, 'expertise');

  // Event-specific information
  const registrationNumber = attendee.badge_number || attendee.registration_number;
  const checkInStatus = attendee.check_in_status;

  // Sensitive fields to exclude from display
  const sensitiveFields = [
    'birth_date', 'birthdate', 'date_of_birth', 'dob',
    'password', 'ssn', 'social_security', 'id_number',
    'passport', 'driver_license', 'credit_card', 'bank_account',
    'salary', 'income', 'medical', 'health', 'emergency_contact'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="secondary"
              onClick={() => window.history.length > 1 ? navigate(-1) : window.close()}
              className="mb-4 gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {window.history.length > 1 ? 'Go Back' : 'Close'}
            </Button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Attendee Profile</h1>
              <p className="text-gray-600 truncate px-4" title={event?.name}>
                {eventId ? `Scanned from QR code for ${event?.name || 'Event'}` : 'Scanned from QR code'}
              </p>
            </div>
          </div>

          {/* Main Profile Card */}
          <Card className="mb-6">
            <Card.Content>
              <div className="text-center space-y-6">
                {/* Avatar */}
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <User className="h-12 w-12 text-white" />
                </div>

                {/* Name */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{name}</h2>
                  {position && (
                    <p className="text-lg text-gray-600 font-medium">{position}</p>
                  )}
                  {organization && (
                    <p className="text-gray-500">{organization}</p>
                  )}
                  {country && (
                    <div className="flex items-center justify-center gap-1.5 mt-1 text-gray-500">
                      <Globe className="h-4 w-4 text-blue-500" />
                      <span>{country}</span>
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t">
                  {email && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Email</p>
                        <a
                          href={`mailto:${email}`}
                          className="font-medium text-blue-600 hover:text-blue-800 break-all underline decoration-dotted hover:decoration-solid transition-all"
                        >
                          {email}
                        </a>
                      </div>
                    </div>
                  )}

                  {phone && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Phone</p>
                        <a
                          href={`tel:${phone.replace(/\s+/g, '')}`}
                          className="font-medium text-blue-600 hover:text-blue-800 underline decoration-dotted hover:decoration-solid transition-all"
                        >
                          {phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {whatsapp && whatsapp !== phone && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                      <Phone className="h-5 w-5 text-green-600" />
                      <div className="flex-1">
                        <p className="text-sm text-green-600">WhatsApp</p>
                        <a
                          href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-green-600 hover:text-green-800 underline decoration-dotted hover:decoration-solid transition-all"
                        >
                          {whatsapp}
                        </a>
                      </div>
                    </div>
                  )}

                  {website && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <Briefcase className="h-5 w-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-600">Website</p>
                        <a
                          href={website.startsWith('http') ? website : `https://${website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:text-blue-800 break-all underline decoration-dotted hover:decoration-solid transition-all"
                        >
                          {website}
                        </a>
                      </div>
                    </div>
                  )}

                  {linkedin && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <User className="h-5 w-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-600">LinkedIn</p>
                        <a
                          href={linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:text-blue-800 break-all underline decoration-dotted hover:decoration-solid transition-all"
                        >
                          {linkedin}
                        </a>
                      </div>
                    </div>
                  )}

                  {twitter && (
                    <div className="flex items-center gap-3 p-3 bg-sky-50 rounded-lg hover:bg-sky-100 transition-colors">
                      <User className="h-5 w-5 text-sky-600" />
                      <div className="flex-1">
                        <p className="text-sm text-sky-600">Twitter</p>
                        <a
                          href={twitter.startsWith('http') ? twitter : `https://twitter.com/${twitter.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-sky-600 hover:text-sky-800 underline decoration-dotted hover:decoration-solid transition-all"
                        >
                          {twitter}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card.Content>
          </Card>

          {/* Professional Information */}
          {(department || specialization || experience || skills) && (
            <Card className="mb-6">
              <Card.Content>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Professional Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {department && (
                      <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                        <Briefcase className="h-5 w-5 text-purple-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-purple-600 font-medium">Department</p>
                          <p className="font-semibold text-gray-900">{department}</p>
                        </div>
                      </div>
                    )}

                    {specialization && (
                      <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg">
                        <User className="h-5 w-5 text-indigo-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-indigo-600 font-medium">Specialization</p>
                          <p className="font-semibold text-gray-900">{specialization}</p>
                        </div>
                      </div>
                    )}

                    {experience && (
                      <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                        <Calendar className="h-5 w-5 text-emerald-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-emerald-600 font-medium">Experience</p>
                          <p className="font-semibold text-gray-900">{experience}</p>
                        </div>
                      </div>
                    )}

                    {skills && (
                      <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                        <Briefcase className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-amber-600 font-medium">Skills & Expertise</p>
                          <p className="font-semibold text-gray-900">{skills}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card.Content>
            </Card>
          )}

          {/* Event Information */}
          {event && (
            <Card className="mb-6">
              <Card.Content>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Event Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Event Name</p>
                        <p className="font-semibold text-gray-900 break-words line-clamp-2" title={event.name}>{event.name}</p>
                      </div>
                    </div>

                    {event.location && (
                      <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                        <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-green-600 font-medium">Location</p>
                          <p className="font-semibold text-gray-900">{event.location}</p>
                        </div>
                      </div>
                    )}

                    {(event.start_date || event.end_date) && (
                      <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                        <Calendar className="h-5 w-5 text-purple-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-purple-600 font-medium">Date</p>
                          <p className="font-semibold text-gray-900">
                            {event.start_date && new Date(event.start_date).toLocaleDateString()}
                            {event.start_date && event.end_date && ' - '}
                            {event.end_date && new Date(event.end_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    )}

                    {event.start_time && (
                      <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                        <Calendar className="h-5 w-5 text-orange-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-orange-600 font-medium">Time</p>
                          <p className="font-semibold text-gray-900">{event.start_time}</p>
                        </div>
                      </div>
                    )}

                    {registrationNumber && (
                      <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                        <Calendar className="h-5 w-5 text-teal-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-teal-600 font-medium">Registration Number</p>
                          <p className="font-semibold text-gray-900">{registrationNumber}</p>
                        </div>
                      </div>
                    )}

                    {checkInStatus && (
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <User className="h-5 w-5 text-slate-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-slate-600 font-medium">Check-in Status</p>
                          <p className={`font-semibold capitalize ${checkInStatus === 'checked_in' ? 'text-green-600' :
                            checkInStatus === 'pending' ? 'text-yellow-600' : 'text-gray-900'
                            }`}>
                            {checkInStatus.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card.Content>
            </Card>
          )}

          {/* Additional Information */}
          {attendee.data && attendee.data.length > 0 && (
            <Card>
              <Card.Content>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {attendee.data
                      .filter(field => {
                        const fieldName = field.field?.field_name || field.custom_field?.field_name;
                        const fieldValue = field.value || field.field_value;

                        // Exclude basic fields already shown
                        const basicFields = ['full_name', 'email', 'phone', 'organization', 'position', 'company', 'job_title'];

                        // Exclude professional fields already shown
                        const professionalFields = ['department', 'specialization', 'field_of_study', 'experience', 'years_experience', 'skills', 'expertise'];

                        // Exclude contact fields already shown
                        const contactFields = ['phone_number', 'whatsapp', 'website', 'website_url', 'linkedin', 'linkedin_url', 'twitter', 'twitter_handle'];

                        // Check if field is sensitive
                        const isSensitive = sensitiveFields.some(sensitive =>
                          fieldName?.toLowerCase().includes(sensitive.toLowerCase())
                        );

                        return fieldName &&
                          fieldValue &&
                          !basicFields.includes(fieldName) &&
                          !professionalFields.includes(fieldName) &&
                          !contactFields.includes(fieldName) &&
                          !isSensitive;
                      })
                      .map((field, index) => {
                        const fieldName = field.field?.field_name || field.custom_field?.field_name;
                        const fieldLabel = field.field?.field_label || field.custom_field?.field_label || fieldName;
                        const fieldValue = field.value || field.field_value;

                        return (
                          <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                            {fieldName?.toLowerCase().includes('country') || fieldName?.toLowerCase().includes('location') || fieldName?.toLowerCase().includes('city') ? (
                              <Globe className="h-4 w-4 text-blue-400 mt-1" />
                            ) : (
                              <Briefcase className="h-4 w-4 text-gray-400 mt-1" />
                            )}
                            <div>
                              <p className="text-sm text-gray-500 capitalize">{fieldLabel}</p>
                              <p className="font-medium text-gray-900 break-words">{fieldValue}</p>
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {attendee.data.filter(field => {
                    const fieldName = field.field?.field_name || field.custom_field?.field_name;
                    const fieldValue = field.value || field.field_value;
                    const basicFields = ['full_name', 'email', 'phone', 'organization', 'position', 'company', 'job_title'];
                    const professionalFields = ['department', 'specialization', 'field_of_study', 'experience', 'years_experience', 'skills', 'expertise'];
                    const contactFields = ['phone_number', 'whatsapp', 'website', 'website_url', 'linkedin', 'linkedin_url', 'twitter', 'twitter_handle'];
                    const isSensitive = sensitiveFields.some(sensitive =>
                      fieldName?.toLowerCase().includes(sensitive.toLowerCase())
                    );
                    return fieldName && fieldValue && !basicFields.includes(fieldName) && !professionalFields.includes(fieldName) && !contactFields.includes(fieldName) && !isSensitive;
                  }).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p>No additional information available</p>
                      </div>
                    )}
                </div>
              </Card.Content>
            </Card>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer variant="registration" />
    </div>
  );
};

export default AttendeeDetail;
